// Intelligent question generation service
// Uses OpenAI API for high-quality educational questions

class QuestionGeneratorService {
  constructor() {
    // In production, you'd get this from environment variables
    this.apiKey = localStorage.getItem('openai_api_key');
    this.baseUrl = 'https://api.openai.com/v1/chat/completions';
    this.conversationContext = new Map(); // Track context per content line
  }

  async generateQuestion(content, context = '') {
    // First try OpenAI if API key is available
    if (this.apiKey) {
      try {
        return await this.generateWithOpenAI(content, context);
      } catch (error) {
        console.warn('OpenAI generation failed, falling back to local:', error);
      }
    }

    // Simple fallback generation
    return this.createSimpleQuestion(content);
  }

  async generateExplanation(content, context = '') {
    // First try OpenAI if API key is available
    if (this.apiKey) {
      try {
        return await this.generateExplanationWithOpenAI(content, context);
      } catch (error) {
        console.warn('OpenAI explanation failed, falling back to local:', error);
      }
    }

    // Simple fallback explanation
    return this.createSimpleExplanation(content);
  }

  async generateWithOpenAI(content, context) {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `You are an expert educational question generator. Create engaging, pedagogically sound questions from educational content.

IMPORTANT: Focus on practical understanding with real-world examples and applications.

Return ONLY a valid JSON object with this structure:
{
  "type": "multiple-choice" | "true-false" | "short-answer" | "fill-blank",
  "question": "Clear, engaging question with real-world context",
  "options": ["A", "B", "C", "D"], 
  "correct": 0 | "answer_string",
  "explanation": "Detailed explanation with real-world example or application",
  "hints": ["practical hint 1", "real-world hint 2"]
}

GUIDELINES:
- Create questions that connect concepts to real-world scenarios
- Include practical examples (Netflix recommendations, GPS navigation, medical diagnosis, etc.)
- Test conceptual understanding, not just memorization
- Make explanations educational and include why this matters in practice
- Use industry examples where relevant (Google, Tesla, medical field, finance, etc.)

For multiple-choice: correct is the index (0-3)
For true-false: options are ["True", "False"], correct is 0 or 1  
For fill-blank: question has _____ blanks, correct is the answer string
For short-answer: correct is "open-ended"`
          },
          {
            role: 'user',
            content: `Generate a question from this educational content:

"${content}"

Context: ${context}

Make it appropriately challenging and educational.`
          }
        ],
        max_tokens: 400,
        temperature: 0.8
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const questionJson = data.choices[0].message.content.trim();
    
    try {
      const questionObject = JSON.parse(questionJson);
      
      // Validate the structure
      if (!questionObject.question || !questionObject.type) {
        throw new Error('Invalid question structure');
      }
      
      return questionObject;
    } catch (parseError) {
      throw new Error('Failed to parse OpenAI response');
    }
  }

  async generateExplanationWithOpenAI(content, context) {
    const response = await fetch(this.baseUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.apiKey}`,
      },
      body: JSON.stringify({
        model: 'gpt-3.5-turbo',
        messages: [
          {
            role: 'system',
            content: `You are an expert educational explainer. Create clear, engaging explanations with real-world examples.

IMPORTANT: Focus on practical understanding with concrete examples from industry.

Return ONLY a valid JSON object with this structure:
{
  "title": "Concise title for the concept",
  "explanation": "Clear explanation of the concept in simple terms",
  "realWorldExample": "Specific real-world example (Netflix, Google, Tesla, etc.)",
  "whyItMatters": "Why this concept is important and valuable to understand",
  "keyTakeaway": "One sentence summary of the main point"
}

GUIDELINES:
- Use simple, conversational language
- Include specific company/technology examples
- Explain practical applications and value
- Make it engaging and relatable
- Focus on understanding rather than memorization`
          },
          {
            role: 'user',
            content: `Explain this educational content with real-world context:

"${content}"

Context: ${context}

Create an engaging explanation that helps someone understand both what this is and why it matters in practice.`
          }
        ],
        max_tokens: 400,
        temperature: 0.7
      })
    });

    if (!response.ok) {
      throw new Error(`OpenAI API error: ${response.status}`);
    }

    const data = await response.json();
    const explanationJson = data.choices[0].message.content.trim();
    
    try {
      const explanationObject = JSON.parse(explanationJson);
      
      // Validate the structure
      if (!explanationObject.title || !explanationObject.explanation) {
        throw new Error('Invalid explanation structure');
      }
      
      return explanationObject;
    } catch (parseError) {
      throw new Error('Failed to parse OpenAI explanation response');
    }
  }

  generateLocallyEnhanced(content, context) {
    const cleanContent = content.trim();
    if (!cleanContent || cleanContent === '[object Object]' || cleanContent.length < 10) {
      return this.createFallbackQuestion(content, context);
    }

    const questions = [];
    
    // Enhanced pattern matching for better questions
    
    // 1. Definition questions (look for "is", "are", "refers to", etc.)
    if (this.containsDefinition(cleanContent)) {
      const definitionQ = this.createDefinitionQuestion(cleanContent);
      if (definitionQ) questions.push(definitionQ);
    }
    
    // 2. Process/method questions
    if (this.containsProcess(cleanContent)) {
      const processQ = this.createProcessQuestion(cleanContent);
      if (processQ) questions.push(processQ);
    }
    
    // 3. Concept application questions
    if (this.containsConcept(cleanContent)) {
      const conceptQ = this.createConceptQuestion(cleanContent);
      if (conceptQ) questions.push(conceptQ);
    }
    
    // 4. Comparison questions
    if (this.containsComparison(cleanContent)) {
      const comparisonQ = this.createComparisonQuestion(cleanContent);
      if (comparisonQ) questions.push(comparisonQ);
    }
    
    // 5. Mathematical questions
    if (this.containsMath(cleanContent)) {
      const mathQ = this.createMathQuestion(cleanContent);
      if (mathQ) questions.push(mathQ);
    }

    // Return best question or fallback
    return questions.length > 0 
      ? questions[Math.floor(Math.random() * questions.length)]
      : this.createFallbackQuestion(cleanContent, context);
  }

  containsDefinition(text) {
    const definitionWords = [/\bis\b/, /\bare\b/, /refers to/, /means/, /defined as/, /called/];
    return definitionWords.some(pattern => pattern.test(text.toLowerCase()));
  }

  containsProcess(text) {
    const processWords = [/steps/, /process/, /method/, /algorithm/, /procedure/, /technique/];
    return processWords.some(pattern => pattern.test(text.toLowerCase()));
  }

  containsConcept(text) {
    const conceptWords = [/principle/, /theory/, /concept/, /idea/, /approach/, /framework/];
    return conceptWords.some(pattern => pattern.test(text.toLowerCase()));
  }

  containsComparison(text) {
    const comparisonWords = [/versus/, /compared to/, /different/, /similar/, /unlike/, /while/];
    return comparisonWords.some(pattern => pattern.test(text.toLowerCase()));
  }

  containsMath(text) {
    const mathWords = [/equation/, /formula/, /calculate/, /solve/, /matrix/, /vector/, /derivative/];
    return mathWords.some(pattern => pattern.test(text.toLowerCase()));
  }

  createDefinitionQuestion(text) {
    const sentences = text.split(/[.!?]+/);
    const definitionSentence = sentences.find(s => 
      /\bis\b|\bare\b|refers to|means|defined as/.test(s.toLowerCase())
    );
    
    if (!definitionSentence) return null;

    const parts = definitionSentence.split(/\bis\b|\bare\b|refers to|means|defined as/i);
    if (parts.length < 2) return null;

    const term = parts[0].trim();
    const definition = parts[1].trim();

    return {
      type: 'multiple-choice',
      question: `What is ${term}?`,
      options: [
        definition,
        this.createWrongOption(definition, 'opposite'),
        this.createWrongOption(definition, 'related'),
        this.createWrongOption(definition, 'unrelated')
      ].sort(() => Math.random() - 0.5),
      correct: 0, // Will be adjusted after shuffling
      explanation: `${term} is ${definition}. Understanding this definition is key to the concept.`,
      hints: ['Focus on the key characteristics', 'Think about what makes this unique']
    };
  }

  createProcessQuestion(text) {
    return {
      type: 'short-answer',
      question: `Describe the main process or method outlined in: "${text}"`,
      answer: 'open-ended',
      explanation: 'This tests your understanding of procedural knowledge and methodology.',
      hints: ['Break it down into steps', 'Consider the sequence of actions']
    };
  }

  createConceptQuestion(text) {
    return {
      type: 'short-answer',
      question: `Explain the key concept and why it's important: "${text}"`,
      answer: 'open-ended',
      explanation: 'This question tests conceptual understanding and practical significance.',
      hints: ['What is the main idea?', 'How does this apply in practice?']
    };
  }

  createComparisonQuestion(text) {
    return {
      type: 'short-answer',
      question: `What comparison or contrast is being made in: "${text}"?`,
      answer: 'open-ended', 
      explanation: 'Understanding comparisons helps identify key differences and relationships.',
      hints: ['Look for contrasting elements', 'Consider what is being compared']
    };
  }

  createMathQuestion(text) {
    return {
      type: 'short-answer',
      question: `Explain the mathematical concept or operation described in: "${text}"`,
      answer: 'open-ended',
      explanation: 'This tests mathematical reasoning and conceptual understanding.',
      hints: ['Focus on the mathematical relationship', 'Consider the underlying principles']
    };
  }

  createWrongOption(correct, type) {
    // Create plausible but incorrect options
    const wrongOptions = {
      opposite: `The opposite of ${correct.toLowerCase()}`,
      related: `A related but different concept involving ${correct.split(' ')[0]}`,
      unrelated: `Something completely different from ${correct.split(' ')[0]}`
    };
    return wrongOptions[type] || 'An incorrect option';
  }

  createFallbackQuestion(content, context) {
    return {
      type: 'short-answer',
      question: `Based on the content "${content.slice(0, 100)}${content.length > 100 ? '...' : ''}", explain the main educational concept being presented.`,
      answer: 'open-ended',
      explanation: 'This open-ended question allows you to demonstrate your understanding of the material.',
      hints: [
        'Focus on the key learning objective',
        'Consider practical applications',
        'Think about why this concept matters'
      ]
    };
  }

  createSimpleQuestion(content) {
    const text = content.trim().toLowerCase();
    const originalText = content.trim();
    
    // Pattern 1: Matrix/Linear Algebra content
    if (text.includes('matrix') || text.includes('vector') || text.includes('linear')) {
      const realWorldExamples = [
        'image processing (like Instagram filters)',
        'Google PageRank algorithm',
        'Netflix recommendation systems',
        '3D graphics in video games',
        'machine learning models'
      ];
      const example = realWorldExamples[Math.floor(Math.random() * realWorldExamples.length)];
      
      return {
        type: 'short-answer',
        question: `How might the concept described in "${originalText.slice(0, 60)}..." be applied in ${example}?`,
        answer: 'open-ended',
        explanation: `Linear algebra concepts are fundamental to modern technology. For example, matrices are used in ${example} to transform data and solve complex problems efficiently.`,
        hints: [`Think about how data transformation applies to ${example}`, 'Consider how mathematical operations can solve real problems']
      };
    }
    
    // Pattern 2: Algorithm/Process content
    if (text.includes('algorithm') || text.includes('process') || text.includes('method') || text.includes('step')) {
      const techExamples = [
        'Google search ranking',
        'Uber route optimization',
        'fraud detection at banks',
        'medical diagnosis systems',
        'self-driving car navigation'
      ];
      const example = techExamples[Math.floor(Math.random() * techExamples.length)];
      
      return {
        type: 'short-answer',
        question: `Imagine you're building ${example}. How would you apply the process described in this content?`,
        answer: 'open-ended',
        explanation: `Understanding algorithms is crucial for solving real-world problems. The process you learned here directly applies to systems like ${example}, where step-by-step problem solving is essential.`,
        hints: ['Think step-by-step like a software engineer', 'Consider the practical constraints of the real world']
      };
    }
    
    // Pattern 3: Data/Statistics content
    if (text.includes('data') || text.includes('analysis') || text.includes('statistical') || text.includes('probability')) {
      const dataExamples = [
        'Netflix analyzing viewing patterns',
        'hospitals predicting patient outcomes',
        'Tesla improving autopilot safety',
        'Amazon optimizing delivery routes',
        'social media detecting misinformation'
      ];
      const example = dataExamples[Math.floor(Math.random() * dataExamples.length)];
      
      return {
        type: 'short-answer',
        question: `How could the data concept from "${originalText.slice(0, 50)}..." help improve ${example}?`,
        answer: 'open-ended',
        explanation: `Data analysis skills are essential in today's world. Companies like ${example.split(' ')[0]} rely on these exact techniques to make better decisions and improve their services.`,
        hints: ['Think about what data would be available', 'Consider how patterns in data lead to insights']
      };
    }
    
    // Pattern 4: Physics content
    if (text.includes('force') || text.includes('energy') || text.includes('motion') || text.includes('wave')) {
      const physicsExamples = [
        'smartphone accelerometers',
        'GPS satellite positioning',
        'medical MRI machines',
        'renewable energy systems',
        'earthquake detection networks'
      ];
      const example = physicsExamples[Math.floor(Math.random() * physicsExamples.length)];
      
      return {
        type: 'short-answer',
        question: `How do engineers apply the physics principle from "${originalText.slice(0, 50)}..." in ${example}?`,
        answer: 'open-ended',
        explanation: `Physics principles are the foundation of modern technology. Understanding concepts like this helps engineers design better ${example} and solve real-world problems.`,
        hints: ['Think about the physical world around you', 'Consider how natural laws apply to technology']
      };
    }
    
    // Pattern 5: Definition questions with real-world context
    if (originalText.includes(' is ') || originalText.includes(' are ')) {
      const parts = originalText.split(/ is | are /);
      if (parts.length >= 2) {
        const industries = ['tech industry', 'healthcare', 'finance', 'entertainment', 'transportation'];
        const industry = industries[Math.floor(Math.random() * industries.length)];
        
        return {
          type: 'short-answer',
          question: `Define ${parts[0].trim()} and give an example of how it's used in ${industry}.`,
          answer: parts[1].trim(),
          explanation: `${parts[0].trim()} ${originalText.includes(' are ') ? 'are' : 'is'} ${parts[1].trim()}. This concept is widely used in ${industry} to solve practical problems and create value.`,
          hints: [`Think about what ${parts[0].trim()} means in practice`, `Consider real applications in ${industry}`]
        };
      }
    }
    
    // Default: Real-world application question
    const shortText = originalText.length > 80 ? originalText.substring(0, 80) + '...' : originalText;
    const applications = [
      'solve a business problem',
      'improve a mobile app',
      'help a medical diagnosis',
      'optimize a delivery system',
      'enhance user experience'
    ];
    const application = applications[Math.floor(Math.random() * applications.length)];
    
    return {
      type: 'short-answer',
      question: `How could you use the concept from "${shortText}" to ${application}?`,
      answer: 'open-ended',
      explanation: `This concept has practical applications across many industries. Thinking about real-world applications helps you understand why these concepts matter and how they create value.`,
      hints: ['Think about practical problems this could solve', 'Consider how professionals use these concepts', 'What real-world challenges does this address?']
    };
  }

  // Answer evaluation and parsing system
  async evaluateAnswer(contentKey, question, userAnswer, originalContent) {
    const context = this.conversationContext.get(contentKey) || { attempts: 0, history: [] };
    
    // Add current attempt to context
    context.attempts += 1;
    context.history.push({ question, answer: userAnswer, timestamp: Date.now() });
    this.conversationContext.set(contentKey, context);

    // Parse answer based on question type and expected format
    const parseResult = await this.parseAnswer(question, userAnswer, originalContent);
    
    if (!parseResult.success) {
      return {
        success: false,
        needsRephrase: true,
        message: parseResult.message,
        suggestions: parseResult.suggestions
      };
    }

    return {
      success: true,
      correct: parseResult.correct,
      message: parseResult.message,
      explanation: parseResult.explanation,
      context: context
    };
  }

  async parseAnswer(question, userAnswer, originalContent) {
    const answer = userAnswer.trim().toLowerCase();
    
    // Pattern 1: YES/NO questions
    if (this.isYesNoQuestion(question)) {
      const yesNoResult = this.parseYesNo(answer);
      if (!yesNoResult.success) {
        return {
          success: false,
          message: "Please answer with 'Yes' or 'No'.",
          suggestions: ["Yes", "No"]
        };
      }
      
      // Use OpenAI to evaluate if available, otherwise basic validation
      if (this.apiKey) {
        return await this.evaluateYesNoWithAI(question, yesNoResult.answer, originalContent);
      } else {
        return { success: true, correct: true, message: "Answer recorded." };
      }
    }

    // Pattern 2: Multiple choice (A, B, C, D)
    if (this.isMultipleChoiceQuestion(question)) {
      const mcResult = this.parseMultipleChoice(answer);
      if (!mcResult.success) {
        return {
          success: false,
          message: "Please answer with A, B, C, or D.",
          suggestions: ["A", "B", "C", "D"]
        };
      }
      return { success: true, correct: true, message: "Answer recorded.", selectedOption: mcResult.option };
    }

    // Pattern 3: Subjective/Open-ended questions
    if (answer.length < 10) {
      return {
        success: false,
        message: "Please provide a more detailed answer (at least 10 characters).",
        suggestions: ["Try to explain your thinking in more detail", "Give specific examples", "Elaborate on your reasoning"]
      };
    }

    // Use OpenAI for subjective evaluation if available
    if (this.apiKey) {
      return await this.evaluateSubjectiveWithAI(question, userAnswer, originalContent);
    }

    // Fallback: Basic validation for subjective answers
    return {
      success: true,
      correct: true,
      message: "Thank you for your thoughtful response!",
      explanation: "Your answer demonstrates engagement with the material."
    };
  }

  isYesNoQuestion(question) {
    const yesNoPatterns = [
      /is (it )?true/i,
      /true or false/i,
      /correct\?/i,
      /do you agree/i,
      /would you say/i
    ];
    return yesNoPatterns.some(pattern => pattern.test(question));
  }

  isMultipleChoiceQuestion(question) {
    return /\b[A-D]\)/i.test(question) || question.includes('A)') || question.includes('a)');
  }

  parseYesNo(answer) {
    const yesWords = ['yes', 'y', 'true', 't', 'correct', 'right', 'agree'];
    const noWords = ['no', 'n', 'false', 'f', 'incorrect', 'wrong', 'disagree'];
    
    if (yesWords.some(word => answer.includes(word))) {
      return { success: true, answer: 'yes' };
    }
    if (noWords.some(word => answer.includes(word))) {
      return { success: true, answer: 'no' };
    }
    return { success: false };
  }

  parseMultipleChoice(answer) {
    const mcPattern = /[abcd]/i;
    const match = answer.match(mcPattern);
    if (match) {
      return { success: true, option: match[0].toLowerCase() };
    }
    return { success: false };
  }

  async evaluateYesNoWithAI(question, userAnswer, originalContent) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: `You are an educational answer evaluator. Evaluate if a student's YES/NO answer is correct based on the educational content.

IMPORTANT: Respond with ONLY a JSON object in this exact format:
{
  "correct": true/false,
  "explanation": "Brief explanation of why the answer is correct or incorrect"
}

Do not include any other text outside the JSON object.`
            },
            {
              role: 'user',
              content: `Educational Content: "${originalContent}"
Question: "${question}"
Student Answer: "${userAnswer}"

Is the student's answer correct? Provide evaluation in the specified JSON format.`
            }
          ],
          max_tokens: 200,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const evaluationText = data.choices[0].message.content.trim();
      
      try {
        const evaluation = JSON.parse(evaluationText);
        return {
          success: true,
          correct: evaluation.correct,
          message: evaluation.correct ? "Correct!" : "Not quite right.",
          explanation: evaluation.explanation
        };
      } catch (parseError) {
        throw new Error('Failed to parse AI evaluation');
      }
    } catch (error) {
      console.warn('AI evaluation failed:', error);
      return { success: true, correct: true, message: "Answer recorded." };
    }
  }

  async evaluateSubjectiveWithAI(question, userAnswer, originalContent) {
    try {
      const response = await fetch(this.baseUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.apiKey}`,
        },
        body: JSON.stringify({
          model: 'gpt-3.5-turbo',
          messages: [
            {
              role: 'system',
              content: `You are an educational answer evaluator. Evaluate if a student's subjective answer demonstrates understanding of the educational content.

IMPORTANT: Respond with ONLY a JSON object in this exact format:
{
  "correct": true/false,
  "quality": "excellent/good/needs_improvement",
  "explanation": "Brief explanation of the answer quality and what was good or could be improved"
}

Consider an answer "correct" (true) if it shows reasonable understanding, even if not perfect.
Do not include any other text outside the JSON object.`
            },
            {
              role: 'user',
              content: `Educational Content: "${originalContent}"
Question: "${question}"
Student Answer: "${userAnswer}"

Evaluate the student's answer quality and understanding. Provide evaluation in the specified JSON format.`
            }
          ],
          max_tokens: 300,
          temperature: 0.3
        })
      });

      if (!response.ok) {
        throw new Error(`OpenAI API error: ${response.status}`);
      }

      const data = await response.json();
      const evaluationText = data.choices[0].message.content.trim();
      
      try {
        const evaluation = JSON.parse(evaluationText);
        const messageMap = {
          excellent: "Excellent answer! You clearly understand the concept.",
          good: "Good answer! You show solid understanding.",
          needs_improvement: "Your answer shows some understanding, but could be more detailed."
        };
        
        return {
          success: true,
          correct: evaluation.correct,
          message: messageMap[evaluation.quality] || "Answer evaluated.",
          explanation: evaluation.explanation
        };
      } catch (parseError) {
        throw new Error('Failed to parse AI evaluation');
      }
    } catch (error) {
      console.warn('AI subjective evaluation failed:', error);
      return {
        success: true,
        correct: true,
        message: "Thank you for your thoughtful response!",
        explanation: "Your answer demonstrates engagement with the material."
      };
    }
  }

  // Get conversation context for a content piece
  getContext(contentKey) {
    return this.conversationContext.get(contentKey) || { attempts: 0, history: [] };
  }

  // Clear context for a content piece
  clearContext(contentKey) {
    this.conversationContext.delete(contentKey);
  }

  createSimpleExplanation(content) {
    const text = content.trim().toLowerCase();
    const originalText = content.trim();
    
    // Pattern-based explanations with real-world context
    if (text.includes('matrix') || text.includes('vector') || text.includes('linear')) {
      return {
        title: "Linear Algebra in Action",
        explanation: `${originalText} This mathematical concept is fundamental to how computers process and transform data.`,
        realWorldExample: "Google uses these concepts in their PageRank algorithm to rank web pages, and Netflix uses them to recommend movies you might like.",
        whyItMatters: "Linear algebra is the backbone of modern AI, computer graphics, data analysis, and countless technologies we use daily.",
        keyTakeaway: "These mathematical tools help computers solve complex real-world problems efficiently."
      };
    }
    
    if (text.includes('algorithm') || text.includes('process') || text.includes('method')) {
      return {
        title: "Algorithms That Power Our World",
        explanation: `${originalText} Algorithms are step-by-step procedures that solve problems systematically.`,
        realWorldExample: "Uber uses route optimization algorithms to find the fastest path to your destination, while banks use fraud detection algorithms to protect your money.",
        whyItMatters: "Understanding algorithms helps you think logically and solve problems efficiently, skills valuable in any field.",
        keyTakeaway: "Algorithms are the recipes that make technology work behind the scenes."
      };
    }
    
    if (text.includes('data') || text.includes('analysis') || text.includes('statistical')) {
      return {
        title: "Data-Driven Decision Making",
        explanation: `${originalText} Data analysis involves finding patterns and insights from information to make better decisions.`,
        realWorldExample: "Spotify analyzes your listening habits to create personalized playlists, while doctors use patient data to improve treatment outcomes.",
        whyItMatters: "In today's world, data literacy is essential for making informed decisions in business, science, and everyday life.",
        keyTakeaway: "Data tells stories that help us understand the world and make smarter choices."
      };
    }
    
    if (text.includes('physics') || text.includes('force') || text.includes('energy')) {
      return {
        title: "Physics in Technology",
        explanation: `${originalText} Physics principles explain how the natural world works and enable technological innovation.`,
        realWorldExample: "Your smartphone's accelerometer uses physics to detect motion, while Tesla's cars apply physics principles for efficient battery management and autonomous driving.",
        whyItMatters: "Physics understanding drives innovation in everything from renewable energy to medical devices to space exploration.",
        keyTakeaway: "Physics principles are the foundation that makes modern technology possible."
      };
    }
    
    // Default explanation
    return {
      title: "Key Concept",
      explanation: `${originalText} This concept is important for building foundational understanding in this subject area.`,
      realWorldExample: "Many companies and professionals use similar concepts to solve practical problems and create value in their work.",
      whyItMatters: "Understanding this concept helps you think more systematically and builds knowledge for more advanced topics.",
      keyTakeaway: "This concept connects to broader principles used across science, technology, and problem-solving."
    };
  }
}

export const questionGenerator = new QuestionGeneratorService();